import React, { forwardRef } from 'react';
import { useId } from '../../hooks/useAccessibility';
import { useRTL } from '../../hooks/useRTL';
import type { InputProps } from '../../types/common';

export const Input = forwardRef<HTMLInputElement, InputProps>(({
  label,
  name,
  type = 'text',
  value,
  placeholder,
  required = false,
  disabled = false,
  error,
  onChange,
  className = '',
  ...props
}, ref) => {
  const inputId = useId('input');
  const errorId = useId('error');
  const { } = useRTL();
  // const descriptionId = useId('description'); // Reserved for future use

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.(e.target.value);
  };

  const inputClasses = [
    'form-input block w-full px-3 py-2 border rounded-md shadow-sm text-start',
    'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
    'disabled:bg-gray-100 disabled:cursor-not-allowed',
    error 
      ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500'
      : 'border-gray-300 placeholder-gray-400',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div className="space-y-1">
      <label
        htmlFor={inputId}
        className={[
          'form-label block text-sm font-medium text-start',
          error ? 'text-red-700' : 'text-gray-700',
          required ? "after:content-['*'] after:ms-0.5 after:text-red-500" : '',
        ].filter(Boolean).join(' ')}
      >
        {label}
      </label>
      
      <input
        ref={ref}
        id={inputId}
        name={name}
        type={type}
        value={value}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        onChange={handleChange}
        className={inputClasses}
        aria-invalid={error ? 'true' : 'false'}
        aria-describedby={error ? errorId : undefined}
        {...props}
      />
      
      {error && (
        <p
          id={errorId}
          className="form-error text-sm text-red-600 text-start"
          role="alert"
          aria-live="polite"
        >
          {error}
        </p>
      )}
    </div>
  );
});

Input.displayName = 'Input';